import {
  Box,
  Typography,
  Container,
  Paper,
  useMediaQuery,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import Logo from "../../../../src/assets/images/logo.png";
import {
  Store,
  Business,
  AccountBalance,
  Description,
  CheckCircle,
} from "@material-ui/icons";

/** Steps */
import StoreStep from "./steps/StoreStep";
import BusinessStep from "./steps/BusinessStep";
import PayoutStep from "./steps/PayoutStep";
import TermsStep from "./steps/TermsStep";

/** Styles */
import "./onboarding.scss";
import { apiJson } from "../../../api/Api";
import { toast } from "react-toastify";

const STEPS = [
  { id: 0, title: "Store", icon: Store },
  { id: 1, title: "Business", icon: Business },
  { id: 2, title: "Payout", icon: AccountBalance },
  { id: 3, title: "Terms", icon: Description },
];

const STEP_COMPONENTS = [StoreStep, BusinessStep, PayoutStep, TermsStep];

const OnboardingPage = (props) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isOnboardingCompleted, setIsOnboardingCompleted] = useState(false);
  const [vendorDetails, setVendorDetails] = useState();

  useEffect(() => {
    document.title = "Account Setup";
  }, []);

  /** Get the current vendor details */
  useEffect(() => {
    const fetchVendor = async () => {
      try {
        const response = await apiJson.vendorProfile.getVendor();
        const step = response?.data?.profile_step;
        setVendorDetails(response.data);
        if (Boolean(response.data?.store?.active)) {
          return props.history.replace("/vendor");
        }

        const stepIndex =
          step === "store"
            ? 1
            : step === "documents"
              ? 2
              : step === "bank_details"
                ? 3
                : step === "terms_conditions"
                  ? 3
                  : 0;

        setCurrentStep(stepIndex);

        // Mark all previous steps as completed
        const completed = Array.from({ length: stepIndex }, (_, i) => i);
        setCompletedSteps(completed);

        setIsOnboardingCompleted(
          Boolean(response.data?.store?.agreement_policy_accepted),
        );
      } catch (error) {
        toast.error(
          error.message || "Something went wrong while fetching vendor details",
        );
      }
    };
    fetchVendor();
  }, []);

  const handleSuccess = () => {
    setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setCurrentStep((prev) => prev - 1);

  // Allow jumping to completed steps OR the current active step
  const handleStepClick = (stepId) => {
    if (completedSteps.includes(stepId) || stepId === currentStep) {
      setCurrentStep(stepId);
    }
  };

  const ActiveStep = STEP_COMPONENTS[currentStep];

  return (
    <div className="onboarding-wrapper">
      {/* <p>{JSON.stringify(vendorDetails)}</p> */}
      <Container maxWidth="md" className="onboarding-container">
        {/* Logo */}
        <Box className="onboarding-logo">
          <img src={Logo} alt="Logo" />
        </Box>

        {/* Header */}
        {!isOnboardingCompleted && (
          <Box className="onboarding-header">
            <Typography className="onboarding-subtitle">
              Complete the steps below to fully activate your vendor account.
            </Typography>
          </Box>
        )}

        {/* Stepper */}
        {!isOnboardingCompleted && (
          <Box className="onboarding-stepper">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isDone = completedSteps.includes(step.id);
              const isActive = currentStep === step.id;
              const isUpcoming = !isDone && !isActive;

              return (
                <React.Fragment key={step.id}>
                  <Box
                    className={`stepper-node ${isActive ? "active" : ""} ${isDone ? "active" : ""} ${isUpcoming ? "upcoming" : ""}`}
                    onClick={() => handleStepClick(step.id)}
                    style={{
                      cursor: isDone || isActive ? "pointer" : "default",
                    }}
                  >
                    <Box className="stepper-circle">
                      {isDone ? (
                        <CheckCircle fontSize="small" />
                      ) : (
                        <Icon fontSize="small" />
                      )}
                    </Box>
                    {!isMobile && (
                      <Typography className="stepper-label">
                        {step.title}
                      </Typography>
                    )}
                  </Box>

                  {/* Connector line between steps */}
                  {index < STEPS.length - 1 && (
                    <Box className={`stepper-line ${isDone ? "done" : ""}`} />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}

        {/* Form Card */}
        <Paper elevation={0} className="onboarding-card">
          {isOnboardingCompleted ? (
            <Box className="onboarding-done">
              <p className="done-icon">🎉</p>
              <Typography className="done-title">You're all set!</Typography>
              <Typography className="done-subtitle">
                Your vendor application has been submitted successfully.
                <br />
                Our onboarding team is now reviewing your business information
                and documents. Your account will be activated shortly after
                approval.
                <br />
                Thank you for choosing alisouq.com⁠ - the trusted marketplace
                for building and construction materials.
                <br />
                <div>
                  Customer Support:
                  <a href="tel:+971543064845">+971 54 306 4845</a>
                  <br />
                  Email:
                  <a href="mailto:customerservice@alisouq.com">
                    customerservice@alisouq.com
                  </a>
                </div>
              </Typography>
            </Box>
          ) : (
            <ActiveStep
              onSuccess={handleSuccess}
              onBack={handleBack}
              isFirstStep={currentStep === 0}
              store={vendorDetails}
              user={vendorDetails}
              setIsOnboardingCompleted={setIsOnboardingCompleted}
            />
          )}
        </Paper>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  width: state.layout.screenWidth,
});

export default withRouter(connect(mapStateToProps)(OnboardingPage));
